/**
 * RenderUnitDetail.jsx
 *
 * Right-pane view for the currently-selected render unit:
 *   1. Unit header strip (title, selector, status, last-rendered, counts).
 *   2. <PreflightBanner /> — green/amber/red gate with blockers & speaker chips.
 *   3. Action bar (Render / Re-render / Stitch / Preview-only).
 *   4. Body — either the live <EyeTestGridPanel/> (eye-test nodes) or the
 *      line-set table.
 *
 * §6.6 A3/A4:
 *   - Status/preflight constants now come from ./renderingConstants (the
 *     mock module is being retired).
 *   - The eye-test slot is fully live: bookId + preflight are threaded down
 *     to <EyeTestGridPanel/> so the in-tree grid renders/polls for real.
 */

import React from "react";
import { Button, Checkbox, Icon, Input, Label, Message, Popup, Segment, Table } from "semantic-ui-react";
import PreflightBanner from "./PreflightBanner";
import SectionVoicePanel from "./SectionVoicePanel";
import EyeTestGridPanel from "./EyeTestGridPanel";
import { LineKindCell, LineKindBulkPicker } from "./LineKindPicker";
import { LineVoiceOfCell, LineVoiceOfBulkPicker } from "./LineVoiceOfPicker";
import { LineEmotionCell, LineEmotionBulkPicker } from "./LineEmotionPicker";
import LineSplitModal from "./LineSplitModal";
import LineTakes from "./LineTakes";
import { PREFLIGHT_STATUS, UNIT_STATUS } from "./renderingConstants";
import { normalizeSpeakerName } from "../../utils/speakerKey";

const STATUS_BADGE = {
  [UNIT_STATUS.COMPLETE]: { color: "green", label: "Complete" },
  [UNIT_STATUS.PARTIAL]: { color: "yellow", label: "Partial" },
  [UNIT_STATUS.NOT_RENDERED]: { color: "grey", label: "Not rendered" },
  [UNIT_STATUS.STALE]: { color: "orange", label: "Stale" },
};

const RESOLUTION_CLASS = {
  voice: "rp-voice",
  category: "rp-category",
  "character-neutral": "rp-character-neutral",
  "narrator-neutral": "rp-narrator-neutral",
  unresolved: "rp-unresolved",
};

const formatTs = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const formatMs = (ms) => {
  if (!ms) return "—";
  return `${(ms / 1000).toFixed(1)}s`;
};

const truncate = (s, n = 72) => {
  if (typeof s !== "string") return "";
  const t = s.trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

/**
 * "Currently rendering" hint shown under the Render button while a job runs.
 * `progress` = { current, done, total } supplied by the page's liveActions:
 *   - `current` is the row the RenderJob loop is actively rendering (status
 *     'rendering'); exactly one at a time. Absent between lines / cache bursts,
 *     when we fall back to the done/total counters.
 */
const RenderProgressHint = ({ progress }) => {
  const { current, done = 0, total = 0 } = progress || {};
  // The in-flight row is the (done+1)th line; clamp so it never exceeds total.
  const position = total ? Math.min(done + 1, total) : done + 1;
  return (
    <div className="rud-render-progress" aria-live="polite">
      <Icon className="loading-icon" name="circle notch" loading />
      {current ? (
        <span className="rud-render-progress-text">
          Rendering line <strong>{position}</strong>
          {total ? (
            <>
              {" "}
              of <strong>{total}</strong>
            </>
          ) : null}
          {" — "}
          {current.chapterNumber != null && (
            <span className="rud-render-progress-ch">Ch.{current.chapterNumber} · </span>
          )}
          <span className="rud-render-progress-speaker">{current.speaker}</span>
          {current.text ? (
            <span className="rud-render-progress-line"> “{truncate(forDisplay(current.text))}”</span>
          ) : null}
        </span>
      ) : (
        <span className="rud-render-progress-text">
          Rendering… <strong>{done}</strong>
          {total ? (
            <>
              {" "}
              of <strong>{total}</strong>
            </>
          ) : null}{" "}
          complete
        </span>
      )}
    </div>
  );
};

/**
 * What a finished render actually did — shown once the job stops, where the progress hint used to
 * vanish mid-count.
 *
 * ⚠️ `done` is printed raw and compared against `total` rather than assumed equal, because the whole
 * point is to be believed: if they ever disagree, the operator must see that rather than a reassuring
 * round number. `skipped`, `cached` and `errors` are named only when non-zero — a clean render should
 * read as one line, not a dashboard.
 */
const RenderFinishedLine = ({ finished }) => {
  const { done = 0, total = 0, skipped = 0, errors = 0, cached = 0, status } = finished || {};
  const complete = total > 0 && done >= total;
  const parts = [];
  if (cached) parts.push(`${cached} already had audio`);
  if (skipped) parts.push(`${skipped} skipped`);
  if (errors) parts.push(`${errors} failed`);
  return (
    <div className="rud-render-finished" aria-live="polite">
      <Icon name={errors ? "warning sign" : complete ? "check circle" : "info circle"}
        color={errors ? "orange" : complete ? "green" : "grey"} />
      <span>
        Render finished — <strong>{done}</strong> of <strong>{total}</strong> line(s)
        {!complete && total > 0 && <> · ⚠️ {total - done} unaccounted for</>}
        {parts.length ? ` · ${parts.join(" · ")}` : ""}
        {status && status !== "complete" ? ` · status: ${status}` : ""}
      </span>
    </div>
  );
};

/**
 * The spoken text as a PERSON should read it.
 *
 * ⭐ A front-matter section speaks its heading and its body in one TTS call, with a `<break>` tag
 * between them asking the model for the beat (`RenderUnitSelector.frontTextForSpeech`). The tag is
 * an instruction to the model, not words of the book — so the grid shows it as a pause glyph rather
 * than as markup. ⛔ DISPLAY ONLY: the tag must survive untouched in the text that is sent to render,
 * so nothing here may write back.
 */
const PAUSE_GLYPH = "⏸";
const forDisplay = (text) =>
  String(text == null ? "" : text)
    .replace(/<break\s+time="([^"]*)"\s*\/>/gi, `${PAUSE_GLYPH}$1`)
    .replace(/\s+/g, " ")
    .trim();

// Wrap case-insensitive occurrences of `q` in the text with <mark> for search hits.
const highlightText = (text, q) => {
  const s = forDisplay(text);
  if (!q) return s;
  const lower = s.toLowerCase();
  const ql = q.toLowerCase();
  if (!lower.includes(ql)) return s;
  const parts = [];
  let i = 0;
  let n = 0;
  for (; ;) {
    const idx = lower.indexOf(ql, i);
    if (idx === -1) { parts.push(s.slice(i)); break; }
    if (idx > i) parts.push(s.slice(i, idx));
    parts.push(<mark key={n++}>{s.slice(idx, idx + q.length)}</mark>);
    i = idx + q.length;
  }
  return parts;
};

const LineRow = ({ row, liveLineActions, outcome, isMatch = true, highlight = "", selectable = false, voicedSpeakers = null }) => {
  // ⭐ EARLIER TAKES, AND ONLY WHERE THERE ARE ANY. renderLine supersedes rather than deletes, so a
  // line can hold the audio of every version it has ever had — but almost every line holds exactly
  // one, and an expander on every row would be noise. `takeCount` gates it.
  const [takesOpen, setTakesOpen] = React.useState(false);
  const rpClass = RESOLUTION_CLASS[row.resolutionPath] || "";
  const isRendered = row.status === "rendered";
  // §6.5+ Patch B — when liveLineActions is supplied AND the row is
  // rendered, the play button becomes live. We require a `uuid` (server
  // primary key on RenderedLine) so the resign thunk can resolve a fresh
  // signed URL. Rows without a uuid (rare, only the LN2 fixture path)
  // fall back to the inert R2.5 button.
  const canPlay = !!liveLineActions?.onPlay && isRendered && !!row.uuid;
  const isBusy =
    !!liveLineActions && liveLineActions.busyLineId === row.lineId;
  // Is THIS row the one loaded in the singleton audio element, and is it
  // playing or paused? Drives the Play / Pause / Stop enabled states.
  const isActive =
    !!liveLineActions && liveLineActions.playingLineId === row.lineId;
  const isPlaying = isActive && !liveLineActions.isPaused;
  const isThisPaused = isActive && !!liveLineActions.isPaused;
  // Compact controls: Play hides only while actively playing (Pause takes its
  // place); Pause shows only while playing; Stop shows whenever loaded. So the
  // cell holds at most two small icon buttons at once.
  const showPlay = !isPlaying;
  const showPause = isPlaying;
  const showStop = isActive;
  return (
    <React.Fragment>
    <Table.Row style={highlight && !isMatch ? { opacity: 0.5 } : undefined}>
      {/*
        TICK A LINE TO REPAIR IT. Before this the smallest repairable thing was a whole UNIT, so
        one dropped word cost a chapter of TTS. Rendered here rather than as a separate mode
        because the defect is noticed WHILE reading this table — a fix that needs you to go
        somewhere else is a fix that mostly does not happen.
      */}
      {selectable && (
        <Table.Cell collapsing className="rud-line-select">
          <Checkbox
            checked={!!liveLineActions?.selectedLineIds?.has(row.lineId)}
            onChange={() => liveLineActions?.onToggleLine?.(row.lineId)}
          />
        </Table.Cell>
      )}
      {/* Consolidated identity column — Line ID, Ch., and Speaker stack
          on three separate lines so the dialogue text column can take the
          freed width. The lineId is mono and truncated with ellipsis;
          hover/focus shows the full string in a Popup so operators can
          still read the suffix during §LR1 captures. */}
      <Table.Cell collapsing className="rud-line-ident">
        <Popup
          size="mini"
          mouseEnterDelay={150}
          content={
            <span className="mono" style={{ wordBreak: "break-all" }}>
              {row.lineId}
            </span>
          }
          trigger={
            <div
              className="rud-line-ident-id mono"
              tabIndex={0}
              aria-label={`Line ID ${row.lineId}`}
            >
              {row.lineId}
            </div>
          }
        />
        {row.chapter !== undefined && (
          <div className="rud-line-ident-ch-row">
            <span className="rud-line-ident-ch">Ch.{row.chapter}</span>
          </div>
        )}
        {/*
          ⭐ THIS ROW IS PART OF AN EDIT, NOT A LINE THE BOOK HAS.

          A chain replaces its anchor, so the anchor is not in the table at all — which means a part left
          unmarked would imply the book contains a line it does not. Shown here, in the identity stack,
          because that is where "which line am I looking at" is already answered.

          ⚠️ Undo lives on every part rather than on one of them: the operator's eye is on the part that
          looks wrong, and making them hunt for ordinal 0 to reverse it is how a fix goes unreversed.
        */}
        {row.editPart && (
          <div className="rud-line-ident-edit-row">
            <Popup
              size="small"
              wide
              content={
                <div style={{ fontSize: "14px", lineHeight: 1.45 }}>
                  <div>
                    Part <strong>{row.editPart.ordinal + 1}</strong> of <strong>{row.editPart.of}</strong>{" "}
                    from a <strong>{row.editPart.kind}</strong>.
                  </div>
                  <div style={{ opacity: 0.8, marginTop: ".35em" }}>
                    The book still has the original line, with its own words and its own audio. Undo puts it
                    back — you will need to re-render it and re-stitch the chapter to hear that.
                  </div>
                </div>
              }
              trigger={<span className="rud-line-edit-chip"><Icon name="cut" size="small" />{row.editPart.kind}</span>}
            />
            {liveLineActions?.onUndoSplit && (
              <button
                type="button"
                className="rud-line-edit-undo"
                disabled={!!liveLineActions.splitBusy}
                onClick={() => liveLineActions.onUndoSplit(row.editPart.anchorLineId)}
                title="Put the original line back"
              >
                undo
              </button>
            )}
          </div>
        )}
        <div className="rud-line-ident-speaker-row">
          {/*
            ⭐ A STAR MEANS THIS SPEAKER HAS A VOICE OF ITS OWN (operator's request, 2026-09-18).
            Reading down a chapter, the question in front of you is "is this one cast yet" — and the
            answer was only available by cross-referencing the preflight chips above. It comes from
            `narratedForWantOfVoice`, not from "resolved": a narrator fallback resolves perfectly well
            and is precisely the case the star must NOT claim.
          */}
          {voicedSpeakers && row.speaker && (
            /* Folded through the shared rule — the set is keyed by the server's own fold, and the
               row's speaker is the raw label straight from `speakerRaw`, which the book spells
               inconsistently. See utils/speakerKey.js. */
            voicedSpeakers.has(normalizeSpeakerName(row.speaker)) ? (
              <Popup size="mini" content="Has a voice of its own."
                trigger={<span className="rud-line-voiced" aria-label="has a voice">*</span>} />
            ) : (
              <Popup size="mini" content="No voice of its own — the Narrator reads these lines."
                trigger={<span className="rud-line-unvoiced" aria-hidden="true">·</span>} />
            )
          )}
          {/*
            The name is ellipsis-clamped in CSS, so the hover is what keeps it readable — the same
            bargain the Line ID above it already makes. This corpus has "Father-Mayor Gullwimple"
            and "diana faye lorris dunnigan" in it; a clamp without a way back to the full string
            would leave the operator guessing which Dunnigan they were looking at.
          */}
          <Popup
            size="mini"
            mouseEnterDelay={150}
            content={row.speaker || "(no speaker)"}
            trigger={<span className="rud-line-ident-speaker">{row.speaker}</span>}
          />
          {/*
            WHAT THE NARRATOR IS STANDING IN FOR.

            A footnote is stamped `speaker: "Sidenote"` — a class of text, not a person — which can
            never be a character, so the line used to resolve to no-character and render SILENT.
            It now falls back to the Narrator, and this says so.

            Shown because the substitution is otherwise invisible: a fallen-back note looks exactly
            like real narration in this table, and "these lines quietly became Narrator" would read
            identically to "these lines were always Narrator". That indistinguishability is how the
            original defect went unnoticed — 36 lines produced no audio and nothing said why.
          */}
          {row.speakerFallbackFrom && (
            <Popup
              size="mini"
              content={`Stamped "${row.speakerFallbackFrom}" — a kind of text, not a character. Read by the Narrator.`}
              trigger={
                <span className="rud-line-ident-speaker-sub" style={{ opacity: 0.6, marginLeft: ".35em" }}>
                  ({row.speakerFallbackFrom})
                </span>
              }
            />
          )}
          {/*
            ⭐ THIS LINE IS SPOKEN IN SOMEBODY ELSE'S VOICE.

            The speaker keeps the line — attribution, the count, the credit — and only the voice is
            borrowed: mimicry, an impersonation, a recording of somebody, a machine cloning whoever it
            has heard. Luck's QPack does it for fifteen chapters.

            Shown for the same reason as its two neighbours, and more urgently than either: a rendered
            line whose audio is Diogenes and whose speaker is the QPack is INDISTINGUISHABLE from a
            misattribution unless the row says the substitution was meant. The arrow reads as
            direction — this speaker, that voice.
          */}
          {row.spokenInVoiceOf && (
            <Popup
              size="small"
              wide
              content={
                <div style={{ fontSize: "14px", lineHeight: 1.45 }}>
                  <div>
                    Spoken by <strong>{row.speaker || "(no speaker)"}</strong>, in{" "}
                    <strong>{row.spokenInVoiceOf}</strong>’s voice.
                  </div>
                  <div style={{ opacity: 0.75, fontSize: "13px", marginTop: ".3em" }}>
                    Not a reassignment — {row.speaker || "the speaker"} still owns this line and its
                    count. Only the voice is borrowed.
                  </div>
                </div>
              }
              trigger={
                <span
                  className="rud-line-ident-voiceof"
                  style={{ marginLeft: ".4em", fontSize: ".8em", color: "#5c6ac4", whiteSpace: "nowrap" }}
                >
                  ▸ in {row.spokenInVoiceOf}’s voice
                </span>
              }
            />
          )}
          {/*
            ⭐ THIS LINE IS NOT SPOKEN AS THE BOOK WRITES IT.

            An editorial correction changes what a line SAYS while leaving the text alone — which is
            the whole design, and also means this table shows the author's words with no hint that
            the audio differs from them. Three weeks later nobody remembers, and a re-render
            reapplies a change nobody can see is in force.

            Same reasoning as the fallback note above it: the invisible substitution is the one that
            has to be announced. Terse by request — a mark and a count, with the detail on hover.
          */}
          {row.editorial && (
            <Popup
              // ⚠️ NOT size="mini" like its neighbours. This popup is the only place the CHANGED WORDS
              // are legible — two quoted phrases to compare, not a one-line label — and mini plus a
              // relative em inside a 0.8em trigger compounded down to something squinted at. An
              // absolute size, set once here, so it cannot be shrunk again by whatever wraps it.
              size="small"
              // ⚠️ WIDE, because the content is two quoted SENTENCES to compare, and a default popup
              // wraps a phrase over four narrow lines — which is what made the comparison hard to read
              // even at 15px. `wide="very"` plus an explicit maxWidth: the prop alone still clamps
              // narrower than a sentence needs.
              wide="very"
              style={{ maxWidth: 560 }}
              content={
                <div style={{ fontSize: "14px", lineHeight: 1.45 }}>
                  <div style={{ marginBottom: ".4em" }}>
                    Spoken differently from the book
                    {row.editorial.kinds?.includes("rewrite") ? " — includes a rewrite" : ""}:
                  </div>
                  {/* BOTH halves, per the operator: what the book says AND what is spoken. A marker
                      that names only the replaced phrase says a line was edited without saying what
                      it now says. */}
                  {(row.editorial.changes || []).map((c, i) => (
                    <div key={`${c.find}-${i}`} style={{ marginBottom: ".2em" }}>
                      {/*
                        ⛔ DARK TONES, BECAUSE THE POPUP IS WHITE. These were #ffb3b3 / #b5f5c0 — pale
                        values that only work on a dark surface, which this is not: on Semantic's
                        default white they washed out almost to nothing. Matched to the reds and greens
                        already used for before/after elsewhere in the app (#a33, #1a7f37).
                      */}
                      <div style={{ color: "#8b1a1a", fontSize: "15px" }}>book: “{c.find}”</div>
                      <div style={{ color: "#12682c", fontSize: "15px" }}>said: “{c.replace}”</div>
                      {c.occurrences > 1 && (
                        <div style={{ opacity: 0.75 }}>{c.occurrences} occurrences in this line</div>
                      )}
                    </div>
                  ))}
                  <div style={{ opacity: 0.75, fontSize: "13px" }}>Edit in the Editorial corrections panel.</div>
                </div>
              }
              trigger={
                <span
                  className="rud-line-ident-editorial"
                  style={{
                    marginLeft: ".4em",
                    fontSize: ".8em",
                    color: row.editorial.kinds?.includes("rewrite") ? "#b58105" : "#7a7a7a",
                    whiteSpace: "nowrap",
                  }}
                >
                  ✎ edited{row.editorial.occurrences > 1 ? ` ×${row.editorial.occurrences}` : ""}
                </span>
              }
            />
          )}
          {/*
            ⭐ "3 takes" — the audio of this line's earlier versions, which SURVIVES a re-render or an
            editorial correction and was unreachable until 2026-09-22. Restoring one costs nothing,
            where re-rendering costs ElevenLabs characters for a file already in S3.
          */}
          {row.takeCount > 1 && (
            <Popup
              size="mini"
              content={`${row.takeCount} takes of this line exist. Earlier ones can be played and restored without spending anything.`}
              trigger={
                <span
                  className="rud-line-ident-takes"
                  onClick={() => setTakesOpen((v) => !v)}
                  style={{ marginLeft: ".4em", fontSize: ".8em", color: "#2185d0", cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  <Icon name={takesOpen ? "caret down" : "caret right"} />{row.takeCount} takes
                </span>
              }
            />
          )}
          {/* ⛔ The panel itself does NOT live here — see the spanning row at the end of this
              component. This cell is 80px wide with `overflow: hidden`, which cropped the Restore
              button off every take. Only the toggle belongs in the identity stack. */}
        </div>
      </Table.Cell>
      {!!liveLineActions?.kindTable?.length && (
        <Table.Cell collapsing className="rud-line-kind">
          <LineKindCell
            row={row}
            kinds={liveLineActions.kindTable}
            onSetKind={liveLineActions.onSetLineKind}
            busy={liveLineActions.kindBusy === row.lineId}
          />
        </Table.Cell>
      )}
      {/* Kind is FORM, this is PERFORMANCE — separate columns because they are separate questions and
          a line can carry both (a recording, in somebody's voice). */}
      {!!liveLineActions?.voiceOfRoster?.length && (
        <Table.Cell collapsing className="rud-line-voiceof">
          <LineVoiceOfCell
            row={row}
            characters={liveLineActions.voiceOfRoster}
            onSetVoiceOf={liveLineActions.onSetLineVoiceOf}
            busy={liveLineActions.voiceOfBusy === row.lineId}
          />
        </Table.Cell>
      )}
      {/*
        ⭐ THE EMOTION IS THE AUDIO TAG, so this cell is a control rather than a readout. It resolves
        through the registry to one of nine presets, and the preset supplies the tag and the voice
        settings — which makes a wrong label the difference between a line that is directed and one that
        is not. `neutral`, the classifier's fallback, is the preset that sends no tag at all.

        ⚠️ Falls back to the plain chip when the vocabulary has not loaded — see LineEmotionPicker.
      */}
      <Table.Cell collapsing className="rud-line-emotion">
        <LineEmotionCell
          row={row}
          vocabulary={liveLineActions?.emotionVocabulary}
          onSetEmotion={liveLineActions?.onSetLineEmotion}
          busy={liveLineActions?.emotionBusy === row.lineId
            || liveLineActions?.emotionBusy === "__bulk__"}
        />
      </Table.Cell>
      <Table.Cell className="line-text">
        {highlightText(row.text, highlight)}
        {/*
          ⭐ SPLIT LIVES ON THE TEXT, because the text is where you see that the line is two lines. The
          ingest glues a Narrator sentence onto a character's dialogue and there is no other symptom — you
          read the row and notice a clause that cannot be the speaker's.

          ⛔ Offered only on a row that is not already part of an edit: chains are flat by design, so a part
          may not itself be split (undo the chain first). And only where there is a second sentence to cut
          at — the modal refuses a mid-sentence cut, because that one would have to be typed.
        */}
        {liveLineActions?.onOpenSplit && !row.editPart && /[.!?…]\s*\S/u.test(String(row.text || "")) && (
          <Popup
            size="mini"
            content="Split this line — the ingest sometimes glues a Narrator sentence onto dialogue. The book keeps its words."
            trigger={
              <button
                type="button"
                className="rud-line-split-btn"
                onClick={() => liveLineActions.onOpenSplit(row)}
                aria-label="Split this line"
              >
                <Icon name="cut" size="small" />
              </button>
            }
          />
        )}
      </Table.Cell>
      <Table.Cell collapsing>
        {outcome && (outcome.status === "failed" || outcome.status === "skipped") ? (
          // Surface the real per-line outcome (from the last render job) instead
          // of the ambiguous "unresolved" default. Hover shows reason + error.
          <Popup
            size="mini"
            wide
            content={
              <div className="rud-outcome-detail">
                <strong>{String(outcome.status).toUpperCase()}</strong>
                {outcome.reason ? <div>reason: {outcome.reason}</div> : null}
                {outcome.error ? <div>error: {outcome.error}</div> : null}
              </div>
            }
            trigger={
              <span className={`resolution-pill rp-unresolved rud-outcome-${outcome.status}`}>
                {outcome.status}
                {outcome.reason ? `: ${outcome.reason}` : ""}
              </span>
            }
          />
        ) : (
          <span className={`resolution-pill ${rpClass}`}>{row.resolutionPath}</span>
        )}
      </Table.Cell>
      <Table.Cell collapsing>{formatMs(row.durationMs)}</Table.Cell>
      <Table.Cell collapsing textAlign="center">
        {/* Compact Play / Pause / Stop. Play swaps out for Pause while playing,
            so there are never more than two small icon buttons. A disabled SUI
            Button emits no hover events, so each sits in a <span> for its
            Popup to still fire. */}
        <div className="line-audio-controls">
          {showPlay && (
            <Popup
              size="mini"
              content={
                canPlay
                  ? isThisPaused
                    ? "Resume playback."
                    : "Play this rendered line."
                  : isRendered
                    ? "Playback unavailable — line missing uuid."
                    : "Not yet rendered."
              }
              trigger={
                <span>
                  <Button
                    icon
                    size="mini"
                    compact
                    basic
                    color={isThisPaused ? "blue" : undefined}
                    disabled={!canPlay}
                    loading={isBusy}
                    onClick={canPlay ? () => liveLineActions.onPlay(row) : undefined}
                    className="line-audio-btn line-play-btn"
                    aria-label={isThisPaused ? "Resume line" : "Play line"}
                  >
                    <Icon name="play" fitted />
                  </Button>
                </span>
              }
            />
          )}
          {showPause && (
            <Popup
              size="mini"
              content="Pause playback."
              trigger={
                <Button
                  icon
                  size="mini"
                  compact
                  color="blue"
                  onClick={() => liveLineActions.onPause()}
                  className="line-audio-btn line-pause-btn"
                  aria-label="Pause line"
                >
                  <Icon name="pause" fitted />
                </Button>
              }
            />
          )}
          {showStop && (
            <Popup
              size="mini"
              content="Stop playback."
              trigger={
                <Button
                  icon
                  size="mini"
                  compact
                  basic
                  onClick={() => liveLineActions.onStop()}
                  className="line-audio-btn line-stop-btn"
                  aria-label="Stop line"
                >
                  <Icon name="stop" fitted />
                </Button>
              }
            />
          )}
          {/*
            CAPTURE THE TEXT YOU JUST HEARD FAIL.

            ⭐ A pronunciation fault is found HERE — listening down the list with the line's text in
            front of you — and it can only be judged in the passage it failed in. The bench's first
            audition searched the book and picked the LONGEST passage carrying the word, which was
            701 characters the operator had never listened to; whatever those takes proved, they
            proved nothing about the line that failed.

            So the capture is the line, verbatim, handed to the bench. Same reason the per-line
            re-render checkbox sits in this table: a fix that needs you to go somewhere else and
            find the text again is a fix that mostly does not happen.
          */}
          {!!liveLineActions?.onCapture && !!row.text && (
            <Popup
              size="mini"
              content="Capture this line's text for a pronunciation audition."
              trigger={
                <Button
                  icon
                  size="mini"
                  compact
                  basic
                  onClick={() => liveLineActions.onCapture(row)}
                  className="line-audio-btn line-capture-btn"
                  aria-label="Capture line text for pronunciation"
                >
                  <Icon name="crosshairs" fitted />
                </Button>
              }
            />
          )}
        </div>
      </Table.Cell>
    </Table.Row>
    {/*
      ⭐ EARLIER TAKES GET THEIR OWN FULL-WIDTH ROW, and that is the whole fix.

      The panel used to render inside the identity cell, which is 80px wide with `overflow: hidden`
      (it has to be — it holds a ~30-char lineId that is ellipsis-clamped). So a panel listing N takes,
      each with an audio player AND a Restore button, was being asked to fit in 80px: the operator saw
      the second button cropped.

      ⛔ Widening the identity column was the tempting fix and it is the wrong one. This table is
      `table-layout: fixed` with `.rud-col-text` the only `auto` column, so every pixel given to a fixed
      column comes out of the dialogue text — and the CSS already records what happens when the fixed
      widths outgrow the panel: *"the Play column left the building — clipped."* A spanning row takes its
      width from nothing at all.

      ⛔ And NOT a Popup, the other option considered: these takes contain audio players, and a
      hover-dismissed popup loses playback the moment the pointer leaves it.

      ⛔ THE SPAN MUST BE THE REAL COLUMN COUNT. `colSpan={99}` was the first attempt, on the assumption
      that browsers simply clamp it. They clamp the RENDERING, not the layout: this table is
      `table-layout: fixed`, where the column count comes from the widest `colSpan` in the table, so 99
      created 90 phantom columns. The header's pixel widths covered the first nine, the phantoms divided
      up the rest, and `.rud-col-text` — the only `auto` column, so the one that absorbs what is left —
      was left with nothing. The operator saw the Text column collapse to 0px the moment a row expanded.

      So it is counted from the SAME conditionals the header uses, and stays beside them for that reason:
      six columns are always present (ident, emotion, text, resolution, dur, play) and three are
      conditional. ⚠️ Chapter is NOT one of them — it lives inside the identity cell, not in a column.
    */}
    {takesOpen && liveLineActions?.bookId && (
      <Table.Row className="rud-takes-row">
        <Table.Cell
          colSpan={
            6
            + (selectable ? 1 : 0)
            + (liveLineActions?.kindTable?.length ? 1 : 0)
            + (liveLineActions?.voiceOfRoster?.length ? 1 : 0)
          }
        >
          <LineTakes
            bookId={liveLineActions.bookId}
            lineId={row.lineId}
            onRestored={liveLineActions.onTakeRestored}
          />
        </Table.Cell>
      </Table.Row>
    )}
    </React.Fragment>
  );
};

/**
 * §6.5 wire-up — `liveActions`, when provided, replaces the primary
 * "Render" button's disabled/inert state with a live click handler. This
 * is the single seam the page uses to drive the render pipeline without
 * adding a new "rs-live-actions" segment. (The R2.5 mockup also drew inert Stitch and Dry-resolve
 * buttons here; both were removed 2026-09-19 — see the note at the end of the action bar.)
 *
 * Shape:
 *   liveActions = {
 *     onRender:  () => void,
 *     disabled?: boolean,
 *     busy?:     boolean,        // shows the SUI loading spinner
 *     label?:    string,         // defaults to "Render"
 *     tooltip?:  string,         // popup body
 *   }
 */
const RenderUnitDetail = ({
  node,
  bookId,
  preflight,
  lineSet,
  lineOutcomes,
  eyeTestSlot,
  liveActions,
  liveLineActions,
  sequenceActions,
  onGridComplete,
  onRefreshUnit,
  preflightLoading = false,
}) => {
  // Text search over the loaded line-set (find a line you heard but can't spot).
  // Hook must run before any early return.
  const [lineSearch, setLineSearch] = React.useState("");

  if (!node) {
    return (
      <Segment placeholder className="rud-empty">
        <div className="rud-empty-inner">
          <Icon name="grid layout" size="huge" color="grey" />
          <div className="rud-empty-title">Select a render unit from the tree</div>
          <div className="rud-empty-sub">
            Pick a chapter, a character, or the eye-test grid to see its
            preflight, line-set, and render actions.
          </div>
        </div>
      </Segment>
    );
  }

  const statusCfg = STATUS_BADGE[node.status] || STATUS_BADGE[UNIT_STATUS.NOT_RENDERED];
  const isRed = preflight && preflight.status === PREFLIGHT_STATUS.RED;
  const showLineSet = lineSet && lineSet.length > 0;
  // Line selection is available only when the page wired it AND the unit actually has lines.
  // Ticks address lineIds, so a unit that emits none has nothing to offer.
  const canSelectLines = !!liveLineActions?.onToggleLine && !!liveLineActions?.onSetLinesSelected;
  const lsq = lineSearch.trim().toLowerCase();
  // When searching, include the 2 lines BEFORE each hit for context (dimmed), so
  // a found line reads in the flow you were following. `isMatch` marks the hit.
  const CONTEXT_BEFORE = 2;
  const rowsToShow = (() => {
    const arr = lineSet || [];
    if (!lsq) return arr.map((row) => ({ row, isMatch: true }));
    const show = new Map(); // idx -> isMatch
    arr.forEach((r, i) => {
      if (forDisplay(r.text).toLowerCase().includes(lsq)) {
        for (let k = Math.max(0, i - CONTEXT_BEFORE); k < i; k++) {
          if (!show.has(k)) show.set(k, false);
        }
        show.set(i, true);
      }
    });
    return [...show.keys()].sort((a, b) => a - b).map((i) => ({ row: arr[i], isMatch: show.get(i) }));
  })();

  // The ids the operator can currently SEE. Select-all works on these, so it respects the filter
  // rather than quietly ticking lines scrolled out of view — which would spend on them.
  const shownIds = canSelectLines ? rowsToShow.map(({ row }) => row.lineId) : [];
  const selectedSet = liveLineActions?.selectedLineIds;
  const shownSelectedCount = selectedSet ? shownIds.filter((id) => selectedSet.has(id)).length : 0;
  const allShownSelected = shownIds.length > 0 && shownSelectedCount === shownIds.length;
  const someShownSelected = shownSelectedCount > 0;
  const selectedCount = selectedSet ? selectedSet.size : 0;
  const matchCount = lsq ? rowsToShow.filter((x) => x.isMatch).length : rowsToShow.length;
  // Sequence-play readiness: every SPEAKABLE line in this unit is rendered (empty lines aren't renderable).
  const speakableRows = (lineSet || []).filter((r) => String(r.text || "").trim());
  const allRendered = speakableRows.length > 0 && speakableRows.every((r) => r.status === "rendered");
  const seqPlaying = !!(sequenceActions && sequenceActions.playing);
  const seqPaused = !!(sequenceActions && sequenceActions.paused);
  /**
   * Speakers that resolve on a voice of their OWN, keyed by the label as the script spells it.
   * Built from the preflight this pane already has, so it costs no request; absent when preflight has
   * not arrived, and the column simply shows nothing rather than guessing.
   */
  const voicedSpeakers = (() => {
    if (!preflight || !Array.isArray(preflight.speakers)) return null;
    const set = new Set();
    preflight.speakers.forEach((sp) => {
      /**
       * ⛔ KEYED ON `normName`, THE SERVER'S OWN FOLD — not on `displayName` (2026-10-09).
       *
       * `displayName` is whichever RAW spelling the preflight happened to meet first in the unit,
       * and the preflight groups by the folded key. So a chapter spelling one speaker two ways —
       * Ashes ch.25 has 2 lines "Glendyl's father" (U+0027) and 2 lines "Glendyl’s father"
       * (U+2019) — produced ONE aggregate carrying ONE of those spellings, and the rows spelled
       * the other way failed the lookup. They showed "·" / "No voice of its own — the Narrator
       * reads these lines" while the render gave them their correct voice.
       *
       * ⚠️ A badge that contradicts the render is worse than no badge: it says the operator has
       * casting work left where there is none, and it says it about a subset of one speaker's
       * lines, which reads as a data problem rather than a display one.
       */
      const key = sp.normName || normalizeSpeakerName(sp.displayName || sp.name);
      if (!key) return;
      if ((sp.narratedForWantOfVoice || 0) === 0 && (sp.resolvedCount || 0) > 0) {
        set.add(key);
      }
    });
    return set;
  })();

  const hasChapterCol =
    showLineSet && lineSet.some((r) => r.chapter !== undefined);

  // Eye-test nodes render their own inline slot (the live R1 grid panel)
  // instead of the line-set table.
  const isEyeTest = node.kind === "eyetestGrid";

  // Non-success outcomes from the last render job for this unit — dev surfacing
  // of skipped/failed lines that otherwise read as a bare "unresolved".
  const outcomeIssues = lineOutcomes
    ? Object.entries(lineOutcomes)
      .filter(([, o]) => o && (o.status === "failed" || o.status === "skipped"))
      .map(([lineId, o]) => ({ lineId, ...o }))
    : [];

  return (
    <div className="render-unit-detail">
      {/* Header strip */}
      <div className="rud-header">
        <div className="rud-header-main">
          <div className="rud-title">{node.label}</div>
          <div className="rud-selector mono">{node.selector}</div>
        </div>
        <div className="rud-header-meta">
          <Label color={statusCfg.color} size="small">
            {statusCfg.label}
          </Label>
          <span className="rud-meta-block">
            <strong>{node.counts.lines.toLocaleString()}</strong> lines ·{" "}
            <strong>{node.counts.chars.toLocaleString()}</strong> chars
          </span>
          {/*
            ⭐ WHAT THIS SELECTION WOULD COST, on the header of the thing you just clicked (operator,
            2026-09-18). The figure was already computed for every unit and shown only inside the
            preflight strip — so "how much is the whole book" meant selecting it, then reading a
            second panel. Cost belongs beside the thing being priced.

            ⚠️ Rendered only when the preflight has ARRIVED. `estCreditsBurnedRoughly` was undefined
            on the wire until today, and a currency formatter given undefined prints "$NaN", which is
            worse than printing nothing: it looks like an answer.
          */}
          {/*
            ⚠️ THE SLOT IS HELD WHILE THE FIGURE IS IN FLIGHT. This block arriving late rewrapped the
            meta row, so the header changed height after the title had already drawn — the second of
            the reflows the operator saw on a Part or Whole Book. A dash of the same width keeps the
            row shape, and rud-meta-spend carries a min-width so the two states cannot differ.
          */}
          {preflightLoading && !preflight && (
            <span className="rud-meta-block rud-meta-spend is-pending">
              <Icon name="dollar sign" fitted />
              <strong>~$—</strong> est.
            </span>
          )}
          {preflight && typeof preflight.estCreditsBurnedRoughly === "number" && (
            <Popup
              size="mini"
              content={
                `${(preflight.estTtsCalls || 0).toLocaleString()} TTS call(s) over `
                + `${(preflight.totalChars || 0).toLocaleString()} characters. A rough magnitude at `
                + `$0.30 per 1,000 characters — ElevenLabs' own usage figures are the real bill.`
              }
              trigger={
                <span className="rud-meta-block rud-meta-spend">
                  <Icon name="dollar sign" fitted />
                  <strong>~${preflight.estCreditsBurnedRoughly.toFixed(2)}</strong> est.
                </span>
              }
            />
          )}
          <span className="rud-meta-block">
            Last rendered:{" "}
            <strong>{node.lastRenderedAt ? formatTs(node.lastRenderedAt) : "never"}</strong>
          </span>
        </div>
      </div>

      {/* Preflight */}
      <PreflightBanner preflight={preflight} onRefresh={onRefreshUnit} loading={preflightLoading} />

      {/* Action bar */}
      <div className="rud-actionbar">
        {liveActions ? (
          <Popup
            size="mini"
            content={liveActions.tooltip || "Start a render for this unit."}
            trigger={
              <span>
                <Button
                  primary
                  icon
                  labelPosition="left"
                  loading={!!liveActions.busy}
                  disabled={!!liveActions.disabled}
                  onClick={liveActions.onRender}
                  className="rud-btn-primary"
                >
                  <Icon name="play" />
                  {liveActions.label || "Render"}
                </Button>
              </span>
            }
          />
        ) : (
          <Popup
            size="mini"
            content={
              !node.executable
                ? "This unit kind is not yet executable."
                : isRed
                  ? "Blocked by preflight. Resolve blockers first."
                  : "Select an executable unit to render."
            }
            trigger={
              <span>
                <Button
                  primary
                  icon
                  labelPosition="left"
                  disabled
                  className="rud-btn-primary"
                >
                  <Icon name="play" />
                  Render
                </Button>
              </span>
            }
          />
        )}
        {/* Sequence play — play this unit's rendered lines in production order (auto-advance + pauses). */}
        {sequenceActions && (seqPlaying ? (
          <>
            <Button icon labelPosition="left" color="teal"
              onClick={seqPaused ? sequenceActions.onResume : sequenceActions.onPause}>
              <Icon name={seqPaused ? "play" : "pause"} />
              {seqPaused ? "Resume" : "Pause"}
            </Button>
            <Button icon labelPosition="left" onClick={sequenceActions.onStop}>
              <Icon name="stop" />
              Stop
            </Button>
          </>
        ) : (
          <Popup
            size="mini"
            content={allRendered
              ? "Play this unit's rendered lines in production order (with production pauses)."
              : "Render all of this unit's lines first — then you can play them in sequence."}
            trigger={
              <span>
                <Button icon labelPosition="left" color="teal" disabled={!allRendered}
                  onClick={() => sequenceActions.onPlaySequence(lineSet)}>
                  <Icon name="play circle" />
                  Play in sequence
                </Button>
              </span>
            }
          />
        ))}
        {/*
          RE-RENDER — live whenever the page supplies a handler.
          Not a duplicate of Render: Render consults the per-line cache and returns the existing
          take for anything already rendered, so it can never replace a bad one. This passes
          `force`, which is the only route to a fresh take — and until it was wired, a defective
          line in front or rear matter could not be fixed from the Studio at all.
          The page confirms before spending, so no guard is needed here.
        */}
        <Popup
          size="mini"
          content={
            liveActions?.onReRender
              ? "Generate every line in this unit again, replacing what is already rendered. Spends credits."
              : "Re-render forces a fresh render (force=true)."
          }
          trigger={
            <span>
              <Button
                icon
                labelPosition="left"
                disabled={!liveActions?.onReRender || !!liveActions.reRenderDisabled}
                onClick={liveActions?.onReRender}
              >
                <Icon name="refresh" />
                Re-render
              </Button>
            </span>
          }
        />
        {/*
          RE-RENDER SELECTED — the smallest repair the pipeline can do.

          Sits beside Re-render deliberately: they are the same action at different scopes, and
          seeing them together is what makes "I only need to fix that one line" occur to you.
          Hidden entirely when nothing is ticked, so the action bar stays quiet until it is useful.
        */}
        {canSelectLines && selectedCount > 0 && (
          <Popup
            size="mini"
            content={
              `Generate ${selectedCount} selected line${selectedCount === 1 ? "" : "s"} again, ` +
              "replacing what is already rendered. Everything else is left alone."
            }
            trigger={
              <span>
                <Button
                  icon
                  labelPosition="left"
                  color="orange"
                  disabled={!liveActions?.onReRenderLines || !!liveActions?.reRenderDisabled}
                  onClick={() => liveActions?.onReRenderLines?.()}
                >
                  <Icon name="refresh" />
                  Re-render selected ({selectedCount})
                </Button>
              </span>
            }
          />
        )}
        {/*
          ⭐ DOWNLOAD SITS BESIDE RE-RENDER because they are the two things done with a ticked set,
          and they are opposites: one replaces the audio, the other takes a copy of it away. Seeing
          them together is what makes "keep this take before I replace it" occur to you.

          ⚠️ NOT gated on the unit being executable or a job being idle, unlike its neighbour: it
          reads what is already rendered and spends nothing. Its own busy flag is the only guard,
          so a second click cannot start a second batch over the first.
        */}
        {canSelectLines && selectedCount > 0 && (
          <Popup
            size="mini"
            content={
              `Save the rendered audio for ${selectedCount} selected line` +
              `${selectedCount === 1 ? "" : "s"} as MP3 files. Lines that have not been rendered ` +
              "are skipped. Costs nothing."
            }
            trigger={
              <span>
                <Button
                  icon
                  labelPosition="left"
                  basic
                  loading={!!liveActions?.downloadBusy}
                  disabled={!liveActions?.onDownloadLines || !!liveActions?.downloadBusy}
                  onClick={() => liveActions?.onDownloadLines?.()}
                >
                  <Icon name="download" />
                  Download MP3 ({selectedCount})
                </Button>
              </span>
            }
          />
        )}
        {/*
          ⭐ ONE FILE, NOT N. Beside Download because it is the same material taken a different way:
          Download keeps each take as its own file; this stitches the ticked lines into a single
          passage — book order, the chapter's own pauses — so a scene can be heard as a scene.
          Spends nothing: it reads audio that is already rendered.
        */}
        {canSelectLines && selectedCount > 0 && (
          <Popup
            size="mini"
            content={
              `Stitch the ${selectedCount} selected line${selectedCount === 1 ? "" : "s"} into ONE MP3, ` +
              "in book order with the chapter's own pauses, and download it. Unrendered lines are left out " +
              "and reported. Costs nothing."
            }
            trigger={
              <span>
                <Button
                  icon
                  labelPosition="left"
                  basic
                  loading={!!liveActions?.stitchLinesBusy}
                  disabled={!liveActions?.onStitchLines || !!liveActions?.stitchLinesBusy}
                  onClick={() => liveActions?.onStitchLines?.()}
                >
                  <Icon name="file audio" />
                  Stitch to one MP3 ({selectedCount})
                </Button>
              </span>
            }
          />
        )}
        {/*
          ⭐ MARK A WHOLE CLUMP AT ONCE. Signage arrives in runs — a street, a terminal, a corridor
          of doors — and the sweep this exists for is 23 speakerless lines in one book. Doing that one
          row at a time is how a sweep gets abandoned half-finished.

          Beside Re-render and Download for the same reason they sit beside each other: these are the
          things done with a ticked set. This one spends nothing and changes no text.
        */}
        {/*
          ⭐ JOIN THE TICKED RUN. Offered only at two or more, because one row is already one line —
          and placed with the other bulk actions rather than on a row, since a join is a statement
          ABOUT a selection and has no single row to hang on. The modal does the explaining; this is
          only the way in. See LineJoinModal and docs/journal/A_FONT_CHANGE_BROKE_THE_SENTENCE.md.
        */}
        {canSelectLines && selectedCount > 1 && !!liveLineActions?.onOpenJoin && (
          <Button
            size="mini"
            basic
            icon
            labelPosition="left"
            loading={liveLineActions.splitBusy === "__join__"}
            disabled={!!liveLineActions.splitBusy}
            /**
             * ⚠️ `_pos` IS THE TABLE'S OWN ORDER, and it is what the dialog checks contiguity
             * against — not `lineIndex`, which the projection does not carry (the line set comes
             * from the script, where lineIndex is a graph property). Rows adjacent in this table ARE
             * adjacent in the book, so this answers the question using what the operator can see.
             *
             * ⭐ The rows also arrive in reading order for the same reason, so the joined preview
             * cannot be built in click order.
             */
            onClick={() => liveLineActions.onOpenJoin(
              rowsToShow
                .map(({ row }, pos) => ({ ...row, _pos: pos }))
                .filter((r) => selectedSet?.has(r.lineId)),
            )}
            style={{ marginRight: ".4em" }}
          >
            <Icon name="compress" />
            Join {selectedCount} rows into one line
          </Button>
        )}
        {canSelectLines && selectedCount > 0 && !!liveLineActions?.kindTable?.length && (
          <LineKindBulkPicker
            kinds={liveLineActions.kindTable}
            count={selectedCount}
            busy={liveLineActions.kindBusy === "__bulk__"}
            onSetKind={(_ids, kind) => liveLineActions.onSetLineKind?.(null, kind)}
          />
        )}
        {/*
          ⭐ SET THE DELIVERY ON EVERY TICKED LINE. A character's habit — a dryness, a nervous hedge —
          is a pattern across dozens of lines, and one row at a time is how such a pass gets abandoned
          half-done. Costs nothing until those lines are re-rendered.
        */}
        {selectedCount > 0 && liveLineActions?.onSetLineEmotion && (
          <LineEmotionBulkPicker
            vocabulary={liveLineActions.emotionVocabulary}
            count={selectedCount}
            busy={liveLineActions.emotionBusy === "__bulk__"}
            onSetEmotion={(_ids, emotion) => liveLineActions.onSetLineEmotion?.(null, emotion)}
          />
        )}
        {/*
          ⭐ AND A BORROWED VOICE ARRIVES IN RUNS TOO — more reliably than signage does. A character
          holds an impression for a scene, not a sentence: the QPack keeps the Diogenes voice across
          four lines of Luck ch15–16, with the narration announcing the shift once at the start and the
          switch back once at the end. Marking a run one line at a time leaves half a scene in the
          wrong voice, which is the version that gets noticed by ear after the render.
        */}
        {canSelectLines && selectedCount > 0 && !!liveLineActions?.voiceOfRoster?.length && (
          <LineVoiceOfBulkPicker
            characters={liveLineActions.voiceOfRoster}
            count={selectedCount}
            busy={liveLineActions.voiceOfBusy === "__bulk__"}
            onSetVoiceOf={(_ids, uuid) => liveLineActions.onSetLineVoiceOf?.(null, uuid)}
          />
        )}
        {/*
          ⛔ TWO MOCKUP BUTTONS REMOVED HERE (2026-09-19): "Stitch to single MP3" and "Dry-resolve",
          both hard-coded `disabled` since the R2.5 visual mockup and never wired. They were not
          harmless: the operator asked what enabled Dry-resolve, and after "Stitch to one MP3 (N)"
          landed, the row held two near-identical stitch buttons of which one could never be pressed.
          A control that is drawn but never works is a question the interface keeps asking.

          Both jobs are done elsewhere now. Stitching a unit is the Stitch panel's; stitching a
          selection is the button above. A dry-resolve of the whole unit IS the preflight banner — it
          runs the resolver over every line without calling ElevenLabs — and a hand-typed
          speaker/emotion probe is the Dry Resolve drawer on the BookWorksheet page.
        */}
      </div>

      {/* Currently-rendering hint — sits under the action bar while a job runs. */}
      {liveActions?.busy && liveActions.progress && (
        <RenderProgressHint progress={liveActions.progress} />
      )}

      {/*
        ⭐ STOP, NEXT TO THE THING IT STOPS. The operator spotted a line needing a join WHILE
        watching it render, so the button belongs against the progress line rather than in an
        action bar above — that is where the eye already is.

        ⚠️ Says what it will actually do. "Stop" on a cooperative stop is a half-truth: the line
        in flight finishes first. The same wording the stitch stop settled on.
      */}
      {liveActions?.busy && liveActions.onStop && (
        <Button
          size="mini"
          basic
          color="red"
          style={{ margin: "0.4em 0" }}
          disabled={!!liveActions.stopping}
          onClick={liveActions.onStop}
          title="Finish the line in flight, then stop. Lines already rendered are kept."
        >
          <Icon name={liveActions.stopping ? "hourglass half" : "stop"} />
          {liveActions.stopping ? "Stopping after this line…" : "Stop render"}
        </Button>
      )}

      {/*
        ⭐ AND THE FINAL COUNT ONCE IT STOPS, because the hint above unmounts before it can show it.
        The operator watched a completed render end on "2074 of 2075" and had to query the graph to
        find out whether the book had finished. A render should say so itself.
      */}
      {!liveActions?.busy && liveActions?.finished && (
        <RenderFinishedLine finished={liveActions.finished} />
      )}

      {/* Dev surfacing — lines that did not produce audio in the last job.
          Shows each line's terminal status + reason/error so a "6/6 done but no
          audio" case is explained instead of reading as a bare "unresolved". */}
      {outcomeIssues.length > 0 && (
        <Message negative size="tiny" className="rud-outcome-banner">
          <Message.Header>
            {outcomeIssues.length} line{outcomeIssues.length > 1 ? "s" : ""} did not render
          </Message.Header>
          <ul className="rud-outcome-list">
            {outcomeIssues.map((o) => (
              <li key={o.lineId}>
                <span className="mono">{o.lineId}</span> — <strong>{o.status}</strong>
                {o.reason ? `: ${o.reason}` : ""}
                {o.error ? ` — ${o.error}` : ""}
              </li>
            ))}
          </ul>
        </Message>
      )}

      {/*
        Back matter carries a casting decision the rest of the tree does not: a ToC-declared
        section renders as its own unit and may be read by someone other than the Narrator. The
        panel appears ONLY here, because this is the one node where that question exists.
      */}
      {node.kind === "rearMatter" && (
        <SectionVoicePanel bookId={bookId} sections={node.sections} />
      )}

      {/* Body — eye-test grid takes over, otherwise line-set table or empty */}
      {isEyeTest ? (
        <div className="rud-eyetest-slot">
          {eyeTestSlot || (
            <EyeTestGridPanel
              bookId={bookId}
              characterId={node.characterId}
              characterLabel={node.label}
              preflight={preflight}
              onGridComplete={onGridComplete}
            />

          )}
        </div>
      ) : showLineSet ? (
        <Segment className="rud-lineset">
          <div className="rud-lineset-head">
            <span className="rud-lineset-title">
              Line set ({lsq
                ? `${matchCount} matching`
                : `${lineSet.length} of ${node.counts.lines.toLocaleString()}`})
            </span>
            <Input
              icon="search"
              size="mini"
              placeholder="Search text…"
              value={lineSearch}
              onChange={(e) => setLineSearch(e.target.value)}
              className="rud-lineset-search"
              style={{ marginLeft: 8 }}
            />
            <span className="rud-lineset-legend">
              <span className="resolution-pill rp-voice">voice</span>
              <span className="resolution-pill rp-category">category</span>
              <span className="resolution-pill rp-character-neutral">
                character-neutral
              </span>
              <span className="resolution-pill rp-narrator-neutral">
                narrator-neutral
              </span>
            </span>
          </div>
          <Table compact celled selectable size="small" className="rud-lineset-table">
            <Table.Header>
              <Table.Row>
                {/*
                  Selects what is SHOWING, not the whole unit — it pairs with the search box
                  above, so "filter, tick all, re-render" is one gesture. That is the shape of
                  the defect classes found by ear.
                */}
                {canSelectLines && (
                  <Table.HeaderCell className="rud-col-select">
                    <Checkbox
                      checked={shownIds.length > 0 && allShownSelected}
                      indeterminate={someShownSelected && !allShownSelected}
                      onChange={() => liveLineActions.onSetLinesSelected(shownIds, !allShownSelected)}
                    />
                  </Table.HeaderCell>
                )}
                {/* Consolidated identity header — was three separate
                    columns (Line ID / Ch. / Speaker) before, now one
                    narrow stack so the Text column gets the freed width. */}
                <Table.HeaderCell className="rud-col-ident">
                  Line {hasChapterCol ? "/ Ch. " : ""}/ Speaker
                </Table.HeaderCell>
                {/*
                  ⭐ ITS OWN COLUMN, next to the identity stack, because a kind is the answer to the
                  question that column raises: a row whose speaker is blank is the row that needs one.
                  Rendered only when the Studio supplied the kinds table — no vocabulary, no column.
                */}
                {!!liveLineActions?.kindTable?.length && (
                  <Table.HeaderCell className="rud-col-kind">Kind</Table.HeaderCell>
                )}
                {/*
                  ⭐ "Voice of" — whose voice comes out, when it is not the speaker's own. Beside Kind
                  because both are properties of the LINE rather than of anybody, and deliberately NOT
                  folded into it: a kind is what the line IS, this is how it is PERFORMED, and a line
                  can carry both. Rendered only when the Studio supplied a roster to borrow from.
                */}
                {!!liveLineActions?.voiceOfRoster?.length && (
                  <Table.HeaderCell className="rud-col-voiceof">Voice of</Table.HeaderCell>
                )}
                <Table.HeaderCell className="rud-col-emotion">Emotion</Table.HeaderCell>
                <Table.HeaderCell className="rud-col-text">Text</Table.HeaderCell>
                <Table.HeaderCell className="rud-col-resolution">Resolution</Table.HeaderCell>
                <Table.HeaderCell className="rud-col-dur">Dur.</Table.HeaderCell>
                <Table.HeaderCell textAlign="center" className="rud-col-play">Play</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rowsToShow.map(({ row, isMatch }) => (
                <LineRow
                  key={row.lineId}
                  row={row}
                  isMatch={isMatch}
                  highlight={lsq}
                  liveLineActions={liveLineActions}
                  voicedSpeakers={voicedSpeakers}
                  selectable={canSelectLines}
                  outcome={lineOutcomes ? lineOutcomes[row.lineId] : null}
                />
              ))}
            </Table.Body>
          </Table>
          {lsq && rowsToShow.length === 0 && (
            <div className="rud-lineset-footer">
              No loaded lines match “{lineSearch.trim()}”.
              {lineSet.length < node.counts.lines
                ? " Note: only the first "
                + lineSet.length.toLocaleString()
                + " of "
                + node.counts.lines.toLocaleString()
                + " lines are loaded — search covers those."
                : ""}
            </div>
          )}
          {!lsq && lineSet.length < node.counts.lines && (
            <div className="rud-lineset-footer">
              Showing first {lineSet.length} of{" "}
              {node.counts.lines.toLocaleString()} lines.
            </div>
          )}
        </Segment>
      ) : (
        <Segment className="rud-empty-hint">
          <Icon name="info circle" /> No line-set preview for this unit yet.
          Executable units expand to their full line-set here.
        </Segment>
      )}
    </div>
  );
};

export default RenderUnitDetail;
